import React, { useContext, useEffect, useState, useRef } from "react";
import { useNavigate, useParams } from "react-router-dom";
import QRCode from "react-qr-code";
import { DataContext } from "../dataContext";
import TransferDetails from "../components/TransferDetails";
import SendSms, { clickSendSms } from "../components/SendSMS";
import BottomButton from "../components/BottomButton";
import SaveFavoritePrompt from "../components/SaveFavoritePrompt";
import { isSMSCapable } from "../utils/utils";
import { formatRecipient } from "../utils/recipients";
import { useTransferApi } from "../api/transferApi";

const TransferSuccessful = ({ branding, homePath }) => {
  const navigate = useNavigate();
  const { history, notificationFeed } = useContext(DataContext);
  const urlParams = useParams();
  const [resendData, setResendData] = useState({});
  const hasSentSms = useRef(false);

  // RfP rows that haven't moved money yet are intentionally excluded from
  // history (they live on the PaymentRequests surface). For the requester
  // arriving here straight after creating an off-network request, history
  // won't have the row — so we also fetch via the per-transfer detail
  // endpoint and fall back to that.
  const { getTransferStatus } = useTransferApi();
  const [directTransfer, setDirectTransfer] = useState(null);
  const [directShortlink, setDirectShortlink] = useState(null);
  const [directFi, setDirectFi] = useState(null);

  const transfer =
    history?.find(({ id }) => id === urlParams.id) || directTransfer;
  const feedNotification = notificationFeed?.find(
    ({ transfer_id }) => transfer_id === urlParams.id
  );
  const isRequestForPay = transfer?.originated_as_rfp === true;
  // Payee Management POC: the recipient opted in to texts, so we texted them the link ourselves —
  // the sender has nothing to send.
  const recipientTexted = directTransfer?.recipient_texted === true && !isRequestForPay;

  useEffect(() => {
    let cancelled = false;
    let attempts = 0;
    const maxAttempts = 10;
    const tryLoad = async () => {
      if (cancelled) return;
      attempts += 1;
      try {
        const data = await getTransferStatus(urlParams.id);
        if (cancelled) return;
        // Always stash the row so the page renders even when history hasn't
        // refreshed yet.
        setDirectTransfer(data);
        const events = data.events || data.getstream_events || [];
        const evt = events.find(
          (e) => e.event === "shortlink_generated" || e.type === "shortlink_generated"
        );
        const url = evt?.url || evt?.data?.shortlink || null;
        if (url) {
          setDirectShortlink(url);
          setDirectFi(
            data.financial_institution || evt?.data?.financial_institution || null
          );
          return;
        }
      } catch (e) {
        // Ignore — we retry a few times.
      }
      if (attempts < maxAttempts && !cancelled) {
        setTimeout(tryLoad, 2000);
      }
    };
    tryLoad();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [urlParams.id]);

  // Synthesized notification — prefers the direct fetch so the page renders
  // data immediately on arrival. Shape matches what the existing code expects.
  const notification = feedNotification || (directShortlink ? {
    transfer_id: urlParams.id,
    shortlink: directShortlink,
    financial_institution: directFi,
    status: "pending",
  } : null);

  // notification/feed doesn't work locally, uncomment an option below

  // // ON NETWORK
  // const notification = notificationFeed.find(
  //   ({ transfer_id }) => transfer_id === "17bfb8a4-2fa3-5615-b4b4-e3cc6151c65d"
  // );

  // const transfer = {
  //   id: urlParams.id,
  //   on_network: true,
  //   status: notification?.status?.toUpperCase(),
  // };

  // // OFF NETWORK
  // const notification = notificationFeed.find(
  //   ({ transfer_id }) => transfer_id === "c35bc840-1865-5bf5-916d-b85ce20589c1"
  // );

  // const transfer = {
  //   amount: notification?.amount,
  //   id: urlParams.id,
  //   note: "Dummy data OFF network succeeded",
  //   on_network: false,
  //   // recipient: { type: "phone", value: "+16155551234" },
  //   recipient: { type: "email", value: "dummy@test.com" },
  //   sender_name: "Chet Wallsworth",
  //   status: "PENDING",
  //   timestamp: "2024-05-21T15:48:54.181114",
  // };
  const canSendSMS = isSMSCapable();

  const shouldShowSmsSend = () => {
    // Wait for this transfer's own record: it's what says whether we already texted the recipient.
    if (!directTransfer || recipientTexted) {
      return false;
    }
    if (transfer) {
      return (
        transfer.recipient?.type === "phone" &&
        canSendSMS &&
        (notification?.shortlink ||
          (notification?.status === "succeeded" &&
            branding?.send_on_network_notification))
      );
    }
    return false;
  };

  useEffect(() => {
    if (isRequestForPay && notification?.shortlink) {
      // RfP off-network: user is asking the other party to pay them.
      setResendData({
        identifier: transfer?.recipient?.value,
        children: "",
        disabled: false,
        message: `I am requesting $${transfer?.amount?.value} from you${notification?.financial_institution ? ` through my account at ${notification.financial_institution}` : ""}${transfer?.note ? ` with the note "${transfer.note}"` : ""}. Click this link to pay: ${notification.shortlink}`,
      });
    } else if (notification?.shortlink) {
      setResendData({
        identifier: transfer?.recipient?.value,
        children: "",
        disabled: false,
        message: `${branding?.sms_customization?.off_net_first || "I am sending you"} $${transfer?.amount?.value} ${branding?.sms_customization?.off_net_second || "from my account at"} ${notification?.financial_institution || ""} ${branding?.sms_customization?.off_net_third || "with the note"} "${transfer?.note || ""}". ${branding?.sms_customization?.off_net_fourth || "Click this link to receive your funds:"} ${notification?.shortlink}`,
      });
    } else if (notification?.status === "succeeded") {
      setResendData({
        identifier: transfer?.recipient?.value,
        children: "",
        disabled: false,
        message: `${branding?.sms_customization?.on_net_first || "I am sending you"} $${transfer?.amount?.value} ${branding?.sms_customization?.on_net_second || "with the note"} "${transfer?.note || ""}". ${branding?.sms_customization?.on_net_third || "The funds will be available shortly, and visible via online banking."}`,
      });
    }
  }, [branding, notification, transfer, isRequestForPay]);

  useEffect(() => {
    // Only attempt to send SMS once, and only when we have all the necessary data
    // Critical: Check resendData.message exists to prevent race condition with the previous useEffect
    if (shouldShowSmsSend() && !hasSentSms.current && resendData.message && resendData.identifier) {
      // Add a small delay to ensure the DOM has updated with the instructions
      const timeoutId = setTimeout(() => {
        hasSentSms.current = true;

        try {
          if (notification?.shortlink) {
            clickSendSms(
              resendData.message,
              resendData.identifier,
              branding?.disable_android_sms
            );
          } else if (branding?.send_on_network_notification) {
            clickSendSms(
              resendData.message,
              resendData.identifier,
              branding?.disable_android_sms
            );
          } else {
            console.warn("sms data issue: missing shortlink or on_network_notification flag");
          }
        } catch (error) {
          console.error("Failed to trigger SMS:", error);
          // Reset flag so user can manually retry via button
          hasSentSms.current = false;
        }
      }, 100); // Small delay to ensure resendData is fully set and DOM is updated

      return () => clearTimeout(timeoutId);
    }
  }, [shouldShowSmsSend, resendData, notification?.shortlink, branding?.send_on_network_notification]);

  const [copied, setCopied] = useState(false);
  const clickCopy = () => {
    navigator.clipboard.writeText(resendData.message);
    setCopied(true);
    setTimeout(() => setCopied(false), 1000);
  };

  const successIcon = (
    <span className="np-hero-icon np-hero-icon-accent" aria-hidden="true">
      <svg
        width="28"
        height="28"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2.2"
        strokeLinecap="round"
        strokeLinejoin="round"
      >
        <polyline points="4 13 9 18 20 6" />
      </svg>
    </span>
  );

  const nonDetailScreen = () => {
    return (
      <div className="np-hero" style={{ flex: 1, justifyContent: "center" }}>
        {successIcon}
        <h1>
          {isRequestForPay
            ? transfer?.outgoing === false
              ? "Payment Sent"
              : "Request Sent"
            : "Success"}
        </h1>
        <p data-cy="transfer_processed_message">
          {isRequestForPay
            ? transfer?.outgoing === false
              ? "Payment was sent"
              : "Payment request sent"
            : "Payment was sent"}
        </p>
        {!isRequestForPay && (
          <div style={{ marginTop: "24px", width: "100%" }}>
            <SaveFavoritePrompt recipient={transfer.recipient} />
          </div>
        )}
      </div>
    );
  };

  const recipientTextedScreen = () => (
    <div>
      <div className="np-hero">
        {successIcon}
        <h1 data-cy="transfer_processed_message">Your money is on its way</h1>
        <p>
          We texted <b>{formatRecipient(transfer.recipient)}</b> a secure link to receive their{" "}
          <b>${transfer?.amount?.value}</b>. There's nothing else you need to do.
        </p>
      </div>
      {!isRequestForPay && transfer.outgoing !== false && (
        <div style={{ marginTop: "24px" }}>
          <SaveFavoritePrompt recipient={transfer.recipient} />
        </div>
      )}
      <div style={{ marginTop: "30px" }}>{TransferDetails(transfer)}</div>
    </div>
  );

  const detailScreen = () => {
    if (recipientTexted) {
      return recipientTextedScreen();
    }
    return (
      <div>
        <div className="np-hero">
          {successIcon}
          <h1 data-cy="transfer_processed_message">
            {isRequestForPay ? (
              transfer.on_network ? (
                <> Your payment request has been sent!</>
              ) : (
                <> Your payment request is ready to be sent!</>
              )
            ) : transfer.on_network ? (
              <> Your transfer has been sent!</>
            ) : (
              <> Your transfer is ready to be sent!</>
            )}
          </h1>
          <p>
            {isRequestForPay && transfer.on_network ? (
              <>
                Have your requestee log in to their sending experience to accept
                the request for payment.
              </>
            ) : isRequestForPay ? (
              transfer.recipient?.type === "phone" ? (
                <>
                  Please text ("SMS") the payment request below so they can pay
                  you...
                </>
              ) : (
                <>A payment request has been sent to their email</>
              )
            ) : transfer.recipient?.type === "phone" ? (
              <>
                Please text ("SMS") the instructions below to the recipient to
                claim funds...
              </>
            ) : (
              <>Instructions have been sent to recipient's email</>
            )}
          </p>
        </div>
        {/* Skip the message / SMS / QR block for on-network RfP — the
            requestee gets an in-app notification + approval screen, nothing
            to text. */}
        {!(isRequestForPay && transfer.on_network) && (
          <>
            <h3 className="np-group-header">
              {isRequestForPay
                ? "Message to your requestee"
                : "Instructions for your recipient"}
            </h3>
            <div className="np-message-card">
              <p>
                {notification?.shortlink ? (
                  <>
                    {resendData.message?.split("http")[0]}
                    <strong>{notification.shortlink}</strong>
                  </>
                ) : (
                  <>{resendData.message}</>
                )}
              </p>
              <button
                type="button"
                aria-label="Copy message"
                className="np-copy-btn"
                onClick={() => clickCopy()}
              >
                <svg
                  width="15"
                  height="15"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="1.8"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  aria-hidden="true"
                >
                  <rect x="9" y="9" width="12" height="12" rx="3" />
                  <path d="M5 15H4a1 1 0 0 1-1-1V4a1 1 0 0 1 1-1h10a1 1 0 0 1 1 1v1" />
                </svg>
              </button>
            </div>
            <div
              id="show_click_copy"
              className="np-copied"
              style={{ display: copied ? "block" : "none", marginBottom: "12px" }}
            >
              Message copied
            </div>
            {shouldShowSmsSend() && (
              <div className="np-actions" style={{ marginBottom: "30px" }}>
                <SendSms
                  {...resendData}
                  disableAndroidSms={branding?.disable_android_sms}
                ></SendSms>
              </div>
            )}
          </>
        )}
        {/* Any off-network shortlink (send or request) can also be claimed
            by scanning it in person. */}
        {notification?.shortlink && (
          <div className="np-qr" data-cy="request_qr_code">
            <div className="np-group-header" style={{ margin: "0 0 10px" }}>
              Or have them scan this QR code
            </div>
            <div className="np-qr-frame">
              <QRCode value={notification.shortlink} size={160} level="M" />
            </div>
          </div>
        )}
        {!isRequestForPay && transfer.outgoing !== false && (
          <div style={{ marginTop: "24px" }}>
            <SaveFavoritePrompt recipient={transfer.recipient} />
          </div>
        )}
        <div style={{ marginTop: "30px" }}>{TransferDetails(transfer)}</div>
      </div>
    );
  };

  return (
    <main className="page_without_nav">
      {transfer?.status ? (
        <>
          {transfer.on_network && !branding.send_on_network_notification ? (
            nonDetailScreen()
          ) : (
            <div style={{ paddingBottom: "20px" }}>{detailScreen()}</div>
          )}
        </>
      ) : (
        <div className="center_page">Transfer Not Found</div>
      )}
      {BottomButton(() => navigate(homePath), "Return home")}
    </main>
  );
};

export default TransferSuccessful;
